import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, User, Mail, Phone, Star, Clock, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClientDashboard = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userReviews, setUserReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  const fetchUserReviews = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/reviews/recent?limit=1000`);
      
      const myReviews = data.filter(review => 
        review.reviewer_name.toLowerCase().includes(user.name.toLowerCase())
      );
      
      setUserReviews(myReviews);
      
      setStats({
        total: myReviews.length,
        pending: myReviews.filter(r => r.status === 'pending').length,
        approved: myReviews.filter(r => r.status === 'approved').length,
        rejected: myReviews.filter(r => r.status === 'rejected').length
      });
    } catch (error) {
      toast.error('Failed to load your reviews');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUserReviews();
    }
  }, [user, fetchUserReviews]);

  const handleLogout = async () => {
    await logout();
    toast.success(t('toast.loggedOut'));
    navigate('/');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-300">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approvata
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 border border-yellow-300">
            <Clock className="w-3 h-3 mr-1" />
            In Attesa
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">
            <XCircle className="w-3 h-3 mr-1" />
            Rifiutata
          </span>
        );
      default:
        return null;
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <h1 className="text-2xl font-black text-blue-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              ForexForum
            </h1>
          </Link>
          <div className="flex gap-3 items-center">
            <LanguageSwitcher />
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              size="sm"
            >
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <Link to="/" className="inline-flex items-center text-blue-900 hover:text-blue-800" data-testid="back-to-home">
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span className="font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('common.backToHome')}</span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-4xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Dashboard Cliente
        </h2>
        <p className="text-slate-600 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Gestisci il tuo profilo e le tue recensioni
        </p>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-10 h-10 text-blue-900" />
                </div>
              </div>
              
              <h3 className="text-2xl font-black text-slate-900 text-center mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Profilo Utente
              </h3>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500 uppercase font-bold">Nome</div>
                    <div className="text-slate-900 font-bold">{user.name}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500 uppercase font-bold">Email</div>
                    <div className="text-slate-900">{user.email}</div>
                  </div>
                </div>

                {user.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500 uppercase font-bold">Telefono</div>
                      <div className="text-slate-900">{user.phone}</div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MessageSquare className="w-4 h-4" />
                    <span>Membro dal {new Date(user.created_at || Date.now()).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mt-6">
              <h3 className="text-xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Statistiche
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Totale Recensioni</span>
                  <span className="text-2xl font-black text-blue-900">{stats.total}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Approvate</span>
                  <span className="text-xl font-bold text-green-700">{stats.approved}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">In Attesa</span>
                  <span className="text-xl font-bold text-yellow-700">{stats.pending}</span>
                </div>
                {stats.rejected > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Rifiutate</span>
                    <span className="text-xl font-bold text-red-700">{stats.rejected}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-2xl font-black text-slate-900 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Le Tue Recensioni
              </h3>

              {userReviews.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 text-lg mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Non hai ancora scritto nessuna recensione
                  </p>
                  <Button
                    onClick={() => navigate('/companies')}
                    className="bg-blue-900 text-white hover:bg-blue-800"
                  >
                    Esplora Broker
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {userReviews.map((review) => (
                    <div
                      key={review.id}
                      data-testid={`user-review-${review.id}`}
                      className="border border-slate-200 rounded-lg p-5"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <Link
                            to={`/company/${review.company_id}`}
                            className="text-xl font-bold text-blue-900 hover:text-blue-800"
                            style={{ fontFamily: 'Chivo, sans-serif' }}
                          >
                            {review.company_name}
                          </Link>
                          <div className="flex items-center gap-3 mt-2">
                            {renderStars(review.rating)}
                            <span className="text-sm text-slate-500">
                              {new Date(review.created_at).toLocaleDateString('it-IT')}
                            </span>
                          </div>
                        </div>
                        {getStatusBadge(review.status)}
                      </div>
                      
                      <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {review.comment}
                      </p>

                      {review.status === 'pending' && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs text-slate-500">
                            ⏳ La tua recensione è in attesa di approvazione dall'amministratore
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
